import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardHeader, CardTitle, CardContent } from './ui/card';
import Notification from './ui/notification';
import { adminAPI } from '../services/api';
import SessionManager from '../utils/sessionManager';
import { Eye, EyeOff } from 'lucide-react';
import './Login.css';

const Login = ({ onLogin }) => {
  const [credentials, setCredentials] = useState({
    username: '',
    password: ''
  });
  const [error, setError] = useState('');
  const [showNotification, setShowNotification] = useState(false);
  const [loading, setLoading] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();

  const handleSessionTimeout = () => {
    SessionManager.clearSession();
    setError('Your session has expired. Please log in again.');
    setShowNotification(true);
    navigate('/login');
  };

  // Check if user is already logged in on component mount
  React.useEffect(() => {
    const storedToken = localStorage.getItem('authToken') || sessionStorage.getItem('authToken');
    const storedUser = localStorage.getItem('adminUser') || sessionStorage.getItem('adminUser');
    
    if (storedToken && storedUser) {
      // Check if session has expired
      if (SessionManager.isSessionExpired()) {
        handleSessionTimeout();
        return;
      }
      
      try {
        const user = JSON.parse(storedUser);
        // Update last activity on successful login restoration
        SessionManager.updateLastActivity();
        onLogin(user);
        navigate('/dashboard');
      } catch (err) {
        // Clear invalid stored data
        handleSessionTimeout();
      }
    }
  }, [onLogin, navigate]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setCredentials(prev => ({
      ...prev,
      [name]: value
    }));
    if (error) {
      setError('');
      setShowNotification(false);
    }
  };

  const showError = (message) => {
    setError(message);
    setShowNotification(true);
  };

  const validateForm = () => {
    if (!credentials.username.trim()) {
      showError('Username is required');
      return false;
    }
    if (!credentials.password.trim()) {
      showError('Password is required');
      return false;
    }
    if (credentials.password.length < 4) {
      showError('Password must be at least 4 characters long');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validate form before submitting
    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      // Call the admin login API
      const response = await adminAPI.loginAdmin(credentials);
      
      console.log('Login response:', response);
      
      // Check if response has data and admin object
      if (response.data && response.data.user) {
        const adminData = response.data.user;
        
        const user = {
          id: adminData.id,
          username: adminData.username || credentials.username,
          email: adminData.email,
          role: adminData.role || 'admin',
          first_name: adminData.first_name,
          last_name: adminData.last_name,
          full_name: adminData.full_name
        };

        // Store authentication token if provided (create a simple token for session management)
        const token = response.data.token || adminData.token || `admin_${adminData.id}_${Date.now()}`;
        
        if (rememberMe) {
          localStorage.setItem('authToken', token);
          localStorage.setItem('adminUser', JSON.stringify(user));
        } else {
          // Use sessionStorage for temporary storage
          sessionStorage.setItem('authToken', token);
          sessionStorage.setItem('adminUser', JSON.stringify(user));
        }

        // Set initial activity timestamp
        SessionManager.updateLastActivity();

        // Call the onLogin callback to update parent state
        onLogin(user);
        
        // Navigate to dashboard
        navigate('/dashboard');
      } else {
        showError('Invalid response from server. Please try again.');
      }
    } catch (err) {
      console.error('Login error:', err);
      
      // Handle different types of errors
      if (err.status === 401) {
        showError('Username or password is wrong');
      } else if (err.message) {
        if (err.message.includes('401') || err.message.includes('Unauthorized')) {
          showError('Username or password is wrong');
        } else if (err.message.includes('404')) {
          showError('Login service not found. Please contact administrator.');
        } else if (err.message.includes('500')) {
          showError('Server error. Please try again later.');
        } else if (err.message.includes('Failed to fetch') || err.message.includes('Network')) {
          showError('Network error. Please check your connection.');
        } else {
          showError(err.message);
        }
      } else {
        showError('Login failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="login-container">
        <div className="login-card">
          <img src="/images/logo.png" alt="ICER Logo" className="login-logo" loading='lazy' />
          <h1>ICER AdminPanel</h1>
          <form onSubmit={handleSubmit} className="login-form">
            <div className="form-group">
              <label htmlFor="username">Username</label>
              <input
                type="text"
                id="username"
                name="username"
                value={credentials.username}
                onChange={handleInputChange}
                required
                disabled={loading}
                placeholder="Enter your username"
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="password">Password</label>
              <div className="password-input-wrapper">
                <input
                  type={showPassword ? 'text' : 'password'}
                  id="password"
                  name="password"
                  value={credentials.password}
                  onChange={handleInputChange}
                  required
                  disabled={loading}
                  minLength={4}
                  placeholder="Enter your password"
                />
                <button
                  type="button"
                  className="password-visibility-button"
                  onClick={() => setShowPassword((visible) => !visible)}
                  disabled={loading}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                  title={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>
            <div className="form-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  disabled={loading}
                />
                <span>Remember me</span>
              </label>
            </div>

            <button 
              type="submit" 
              className="login-button"
              disabled={loading || !credentials.username.trim() || !credentials.password.trim()}
            >
              {loading ? (
                <>
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white inline" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Logging in...
                </>
              ) : (
                'Login'
              )}
            </button>
          </form>
        </div>
      </div>

      <Notification
        type="error"
        message={error}
        isVisible={showNotification}
        onClose={() => setShowNotification(false)}
        position="top-center"
        duration={5000}
      />
    </>
  );
};

export default Login;
